import Link from "next/link";
import { ReactNode } from "react";

type ButtonVariant = "primary" | "secondary" | "ghost" | "white" | "outline-white";
type ButtonSize = "default" | "lg" | "xl";

interface ButtonProps {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  href?: string;
  target?: string;
  className?: string;
  onClick?: () => void;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    "bg-primary text-white shadow-[0_4px_14px_rgba(124,58,237,0.25)] hover:bg-primary-hover hover:-translate-y-0.5 hover:shadow-[0_6px_20px_rgba(124,58,237,0.35)]",
  secondary:
    "bg-white text-text border border-border shadow-sm hover:bg-bg-alt",
  ghost: "bg-transparent text-text-muted hover:text-text hover:bg-bg-alt",
  white:
    "bg-white text-primary shadow-[0_8px_24px_rgba(0,0,0,0.15)] hover:-translate-y-0.5 hover:shadow-[0_12px_32px_rgba(0,0,0,0.2)]",
  "outline-white":
    "bg-transparent text-white border-2 border-white/30 hover:bg-white/10 hover:border-white/50",
};

const sizeStyles: Record<ButtonSize, string> = {
  default: "px-5 py-2.5 text-sm rounded-lg",
  lg: "px-7 py-3.5 text-[15px] rounded-xl",
  xl: "px-9 py-4.5 text-base rounded-[14px]",
};

export function Button({
  children,
  variant = "primary",
  size = "default",
  href,
  target,
  className = "",
  onClick,
}: ButtonProps) {
  const baseStyles =
    "inline-flex items-center justify-center gap-2 font-semibold cursor-pointer transition-all duration-200 no-underline border-none font-[inherit]";

  const combinedStyles = `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`;

  if (href) {
    return (
      <Link href={href} target={target} rel={target === "_blank" ? "noopener noreferrer" : undefined} className={combinedStyles}>
        {children}
      </Link>
    );
  }

  return (
    <button onClick={onClick} className={combinedStyles}>
      {children}
    </button>
  );
}
